<script lang="ts" context="module">
  import { onDestroy, onMount } from 'svelte';
  import { getAppConfig } from '../../../common/config/app-config';

  const { extraThemeResourcePath } = getAppConfig();
</script>

<script lang="ts">
  import { getStr, globalCurrencyTag, parseExtraCareData, slideOpenAnimation } from '../../../common/util';
  import Icon from '../../common/components/Icon.svelte';
  import { pdpCareServiceSelectedItem, pdpServicesSaveState, setMixpanelDataForService } from '../store/pdp_store';
  import PdpSafeguardTile from './PDPSafeguardTile.svelte';
  import PdpServiceMiniCard from './PDPServiceMiniCard.svelte';

  export let data: any;
  export let labelData: any;
  export const lmdConfigurations: any = [];

  let activeTabIndex = -1;
  let activeTabSelectedOptionIndex = -1;
  let activeTabChildSelected = false;

  $: tabDisplay = data?.tabDisplay;
  $: activeTabSelectedOptionChild =
    (tabDisplay && extraCareData[activeTabIndex]?.children[activeTabSelectedOptionIndex]?.child) || null;
  $: extraCareData = (tabDisplay && parseExtraCareData(data)) || [];

  onMount(() => {
    // if saved state contains a selected item, preselect it
    const saveState = $pdpServicesSaveState.care;

    if (saveState) {
      ({ activeTabIndex = -1, activeTabSelectedOptionIndex = -1, activeTabChildSelected = false } = saveState);
    }
  });
  onDestroy(() => {
    const saveStateFn = pdpServicesSaveState.setCare;

    saveStateFn(activeTabIndex, activeTabSelectedOptionIndex, activeTabChildSelected);
  });

  function selectTab(tabDataIndex) {
    console.log('[ExtraCare] selectTab tabDataIndex activeTabIndex:', tabDataIndex, activeTabIndex);
    if (tabDataIndex !== activeTabIndex) {
      activeTabIndex = tabDataIndex;
    } else {
      activeTabIndex = -1;
    }
    selectActiveTabOption(-1);
  }
  function selectActiveTabOption(activeTabOptionIndex) {
    console.log('[ExtraCare] selectActiveTabOption activeTabOptionIndex:', activeTabOptionIndex);
    if (activeTabSelectedOptionIndex !== activeTabOptionIndex) {
      activeTabSelectedOptionIndex = activeTabOptionIndex;
    } else {
      activeTabSelectedOptionIndex = -1;
    }
    activeTabChildSelected = false;
    updatePDPStoreWithSelectedItem(activeTabSelectedOptionIndex, activeTabChildSelected);
  }
  function toggleActiveTabChildOption() {
    activeTabChildSelected = !activeTabChildSelected;
    updatePDPStoreWithSelectedItem(activeTabSelectedOptionIndex, activeTabChildSelected);
  }

  function updatePDPStoreWithSelectedItem(activeTabSelectedOptionIndex, activeTabChildSelected: boolean) {
    const activeTab = (activeTabIndex > -1 && extraCareData[activeTabIndex]) || null;
    const activeItem = tabDisplay
      ? activeTab &&
        (activeTabChildSelected
          ? activeTab.children[activeTabSelectedOptionIndex]?.child
          : activeTab.children[activeTabSelectedOptionIndex])
      : data.vasPrices[activeTabSelectedOptionIndex];

    const {
      noPlanProductCode,
      serviceProductCode,
      combinedVas,
      vasGroup,
      showApplyService,
      upgradeVasEntry,
      installService,
    } = data;
    const {
      CSRFToken,
      productDetails: { code: pdpProductCode },
    } = getAppConfig();

    const targetStore = pdpCareServiceSelectedItem;

    if (activeItem) {
      const {
        noPlanOption,
        vasType: { code },
        priceType,
        priceTo,
        productCodes,
        additionalAwsProductCode,
        additionalAwsVasGroup,
        defaultWarranty,
      } = activeItem;
      const vasProductCode = noPlanOption ? noPlanProductCode : (productCodes ?? serviceProductCode);

      setMixpanelDataForService(
        'Care',
        activeItem?.vasType?.label || 'no plan',
        activeItem?.vasType?.label || 'no addon',
        0,
        vasProductCode
      );

      targetStore.set({
        vasProductCode,
        vasType: code,
        priceType,
        priceTo,
        productCode: pdpProductCode,
        additionalAwsProductCode,
        additionalAwsVasGroup,
        combinedVas,
        qty: 1,
        vasGroup,
        showApplyService,
        CSRFToken,
        defaultWarranty,
        upgradeService: upgradeVasEntry,
        installService,

        //noPlanOption
        ...(noPlanOption && { noPlanOption }),
      });
    } else {
      setMixpanelDataForService('Care'); // no param supplied to nullify it
      targetStore.set(null);
    }
  }
</script>

<PdpServiceMiniCard data={labelData} serviceBlockType={data.serviceBlockType}>
  <section class="pdp-extra-care-service">
    {#if tabDisplay}
      <!-- tabDisplay true, render with tabs -->
      <section class="base-tab-list">
        {#each extraCareData as tabData, tabIndex}
          <button
            class="extra-care-tab"
            class:active={activeTabIndex === tabIndex}
            on:click={() => selectTab(tabIndex)}
          >
            <h3 class="label">{tabData.vasTypeGroupName}</h3>
            <p class="subtitle">
              <!-- Starts from SAR XXX -->
              {@html getStr('extra.care.tab.starts.from', [globalCurrencyTag, tabData.minPrice])}
            </p></button
          >
        {/each}
      </section>
      {#each extraCareData as tabData, tabIndex}
        {#if activeTabIndex === tabIndex}
          <section class="active-tab-content" transition:slideOpenAnimation>
            <p class="active-tab-subtitle">
              <!-- Now go ahead and pick what fits you best! -->
              {getStr('extra.care.tab.plans.taxt')}
            </p>
            <section class="active-tab-options">
              {#each tabData.children as activeTabOption, activeTabOptionIndex}
                <PdpSafeguardTile
                  addonView={false}
                  data={activeTabOption}
                  active={activeTabOptionIndex === activeTabSelectedOptionIndex}
                  on:click={() => selectActiveTabOption(activeTabOptionIndex)}
                />
              {/each}
            </section>
          </section>
        {/if}
      {/each}
      {#if !!activeTabSelectedOptionChild}
        <button
          on:click={toggleActiveTabChildOption}
          class="active-tab-content-child"
          transition:slideOpenAnimation
          class:active={activeTabChildSelected}
        >
          <img src="{extraThemeResourcePath}/images/extra-care-yellow-bg-img.svg" width="30" height="46" alt="" />
          <div class="message">
            <!-- @Add SAR {activeTabSelectedOptionChild.priceDifference} now & enjoy {activeTabSelectedOptionChild.vasType.years} years of eXtra care benefits! -->
            {@html getStr('extra.care.upgrade.plans.taxt', [
              globalCurrencyTag,
              activeTabSelectedOptionChild.priceDifference,
              activeTabSelectedOptionChild.vasType.name,
            ])}
          </div>
          <section class="check active-icon">
            {#if activeTabChildSelected}
              <Icon glyph="check" type="font" desktopSize={24} />
            {:else}
              <section class="check-placeholder"></section>
            {/if}
          </section>
        </button>
      {/if}
    {:else}
      <!-- tabDisplay false, render like other services -->
      <section class="active-tab-content" transition:slideOpenAnimation>
        <section class="active-tab-options">
          {#each data.vasPrices as activeTabOption, activeTabOptionIndex}
            <PdpSafeguardTile
              addonView={false}
              data={activeTabOption}
              active={activeTabOptionIndex === activeTabSelectedOptionIndex}
              on:click={() => selectActiveTabOption(activeTabOptionIndex)}
            />
          {/each}
        </section>
      </section>
    {/if}
  </section>
</PdpServiceMiniCard>

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  .base-tab-list {
    display: flex;
    gap: 8px;
  }
  .extra-care-tab {
    background: $primary-white;
    border: 1px solid $secondary-gray-02;
    border-radius: 12px;
    padding: 12px 16px;
    flex-grow: 1;
    text-align: left;

    &.active {
      border-color: $primary-blue;
    }

    .label,
    .subtitle {
      font-size: 12px;
      line-height: 14px;
      font-weight: 400;
      color: $neutral-dark-03;
      margin: 0;
    }
    .subtitle {
      margin-top: 8px;

      strong {
        color: $primary-black;
      }
    }
  }

  .active-tab-content {
    padding-top: 12px;
  }

  .active-tab-options {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    & > :global(button.vas-price-tile) {
      flex-basis: calc(50% - 4px);
      flex-grow: 0;
    }
  }

  .active-tab-content-child {
    margin-top: 16px;
    border: 1px solid $functional-yellow;
    border-radius: 8px;
    background-color: rgba($functional-yellow, 10%);
    display: flex;
    align-items: center;
    gap: 16px;
    padding-inline: 10px;
    cursor: pointer;
    text-align: left;
    width: 100%;

    .message {
      font-size: 12px;
      line-height: 14px;
      font-weight: 700;
      color: $neutral-dark-01;
    }
    .check {
      color: $primary-blue;
      margin-left: auto;

      .check-placeholder {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 1px solid $primary-blue;
        margin-inline: 2px;
      }
    }

    &:not(.active) {
      :global(i::after) {
        content: '';
        position: absolute;
        inset: 0;
        border: 1px solid red;
        border-radius: 50%;
        display: block;
        top: 4px;
        left: 2px;
        right: 2px;
        bottom: 4px;
        background: white;
      }
    }
  }
</style>
